import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { useTeacherContext } from "@/lib/teacher-context";
import { supabase } from "@/integrations/supabase/client";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/teacher/reports")({
  ssr: false,
  beforeLoad: () => requireRole("teacher"),
  head: () => ({ meta: [{ title: "Reports — MAKAI" }] }),
  component: ReportsPage,
});

type Tab = "performance" | "attendance" | "progress";

function ReportsPage() {
  const ctx = useTeacherContext();
  const [tab, setTab] = useState<Tab>("performance");
  const [streamId, setStreamId] = useState("");

  useEffect(() => { if (!streamId && ctx.streams.length) setStreamId(ctx.streams[0].id); }, [ctx.streams, streamId]);

  return (
    <AppShell role="teacher" title="Reports" subtitle="Class performance, attendance and learner progress.">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-border bg-card p-1 text-sm shadow-soft">
          {[["performance","Performance"],["attendance","Attendance"],["progress","Progress"]].map(([id,l]) => (
            <button key={id} onClick={() => setTab(id as Tab)}
              className={`rounded-full px-3 py-1.5 font-semibold transition ${tab === id ? "bg-gradient-african text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        <select value={streamId} onChange={(e) => setStreamId(e.target.value)} className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
          {ctx.streams.map((s) => <option key={s.id} value={s.id}>{s.class_name} · {s.name}</option>)}
        </select>
      </div>

      {streamId && tab === "performance" && <Performance streamId={streamId} />}
      {streamId && tab === "attendance" && <Attendance streamId={streamId} />}
      {streamId && tab === "progress" && <Progress streamId={streamId} />}
    </AppShell>
  );
}

function Performance({ streamId }: { streamId: string }) {
  const [rows, setRows] = useState<{ subject: string; avg: number; count: number }[]>([]);
  useEffect(() => {
    (async () => {
      const { data: asmts } = await supabase.from("assessments")
        .select("id, max_score, subject:subjects_catalog(id, name)").eq("stream_id", streamId);
      const ids = (asmts ?? []).map((a: any) => a.id);
      if (!ids.length) { setRows([]); return; }
      const { data: marks } = await supabase.from("assessment_marks").select("assessment_id, score").in("assessment_id", ids);
      const subjMap = new Map<string, { name: string; sum: number; count: number }>();
      const byA = new Map((asmts ?? []).map((a: any) => [a.id, a]));
      for (const m of marks ?? []) {
        if (m.score == null) continue;
        const a: any = byA.get(m.assessment_id); if (!a?.subject) continue;
        const pct = (Number(m.score) / Number(a.max_score || 100)) * 100;
        const g = subjMap.get(a.subject.id) ?? { name: a.subject.name, sum: 0, count: 0 };
        g.sum += pct; g.count += 1; subjMap.set(a.subject.id, g);
      }
      setRows(Array.from(subjMap.values()).map((g) => ({ subject: g.name, avg: g.sum / Math.max(1, g.count), count: g.count })));
    })();
  }, [streamId]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <h3 className="mb-3 font-display text-lg font-bold">Class average by subject</h3>
      {rows.length === 0 ? <div className="text-sm text-muted-foreground">No marks yet.</div> : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.subject}>
              <div className="flex justify-between text-sm"><span className="font-medium">{r.subject}</span><span className="text-muted-foreground">{r.avg.toFixed(0)}% · {r.count} marks</span></div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-gradient-african" style={{ width: `${Math.min(100, r.avg)}%` }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Attendance({ streamId }: { streamId: string }) {
  const [rows, setRows] = useState<{ learner: string; present: number; absent: number; late: number; pct: number }[]>([]);
  useEffect(() => {
    (async () => {
      const since = new Date(Date.now() - 30 * 86400_000).toISOString().slice(0, 10);
      const [{ data: learners }, { data: att }] = await Promise.all([
        supabase.from("learners").select("id, first_name, last_name").eq("current_stream_id", streamId).eq("status","active"),
        supabase.from("attendance").select("learner_id, status").eq("stream_id", streamId).gte("date", since),
      ]);
      const agg = new Map<string, { p: number; a: number; l: number; t: number }>();
      for (const r of att ?? []) {
        const g = agg.get(r.learner_id) ?? { p: 0, a: 0, l: 0, t: 0 };
        g.t += 1;
        if (r.status === "present") g.p += 1;
        else if (r.status === "absent") g.a += 1;
        else if (r.status === "late") g.l += 1;
        agg.set(r.learner_id, g);
      }
      setRows((learners ?? []).map((l: any) => {
        const g = agg.get(l.id) ?? { p: 0, a: 0, l: 0, t: 0 };
        return {
          learner: `${l.first_name} ${l.last_name}`,
          present: g.p, absent: g.a, late: g.l,
          pct: g.t ? Math.round(((g.p + g.l) / g.t) * 100) : 0,
        };
      }).sort((a, b) => a.pct - b.pct));
    })();
  }, [streamId]);

  function exportCsv() {
    const csv = [["Learner","Present","Late","Absent","Attendance %"], ...rows.map((r) => [r.learner, r.present, r.late, r.absent, r.pct])]
      .map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "attendance-report.csv"; a.click(); URL.revokeObjectURL(url);
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-display text-lg font-bold">Attendance (last 30 days)</h3>
        <Button variant="outline" size="sm" onClick={exportCsv}><Download className="mr-1 h-4 w-4" /> Export</Button>
      </div>
      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
            <tr><th className="px-3 py-2">Learner</th><th className="px-3 py-2 text-center">Present</th><th className="px-3 py-2 text-center">Late</th><th className="px-3 py-2 text-center">Absent</th><th className="px-3 py-2 text-center">%</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.learner} className="border-t border-border">
                <td className="px-3 py-2 font-medium">{r.learner}</td>
                <td className="px-3 py-2 text-center">{r.present}</td>
                <td className="px-3 py-2 text-center">{r.late}</td>
                <td className="px-3 py-2 text-center">{r.absent}</td>
                <td className={`px-3 py-2 text-center font-semibold ${r.pct >= 90 ? "text-primary" : r.pct >= 75 ? "text-gold-foreground" : "text-destructive"}`}>{r.pct}%</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-muted-foreground">No data.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Progress({ streamId }: { streamId: string }) {
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => {
    (async () => {
      const [{ data: learners }, { data: asmts }, { data: subs }] = await Promise.all([
        supabase.from("learners").select("id, first_name, last_name").eq("current_stream_id", streamId).eq("status","active"),
        supabase.from("assessments").select("id, max_score").eq("stream_id", streamId),
        supabase.from("assignments").select("id").eq("stream_id", streamId),
      ]);
      const aIds = (asmts ?? []).map((a: any) => a.id);
      const subIds = (subs ?? []).map((s: any) => s.id);
      const [{ data: marks }, { data: submissions }] = await Promise.all([
        aIds.length ? supabase.from("assessment_marks").select("learner_id, score, assessment_id").in("assessment_id", aIds) : Promise.resolve({ data: [] } as any),
        subIds.length ? supabase.from("assignment_submissions").select("learner_id, status").in("assignment_id", subIds) : Promise.resolve({ data: [] } as any),
      ]);
      const byA = new Map((asmts ?? []).map((a: any) => [a.id, a]));
      const out = (learners ?? []).map((l: any) => {
        const myMarks = (marks ?? []).filter((m: any) => m.learner_id === l.id && m.score != null);
        const avg = myMarks.length
          ? myMarks.reduce((s: number, m: any) => s + (Number(m.score) / Number((byA.get(m.assessment_id) as any)?.max_score || 100)) * 100, 0) / myMarks.length
          : null;
        const mySubs = (submissions ?? []).filter((s: any) => s.learner_id === l.id);
        const done = mySubs.filter((s: any) => s.status === "submitted" || s.status === "graded").length;
        const completion = subIds.length ? Math.round((done / subIds.length) * 100) : 0;
        return { name: `${l.first_name} ${l.last_name}`, avg, completion };
      });
      setRows(out);
    })();
  }, [streamId]);

  return (
    <div className="grid gap-3 md:grid-cols-2">
      {rows.map((r) => (
        <div key={r.name} className="rounded-2xl border border-border bg-card p-4 shadow-soft">
          <div className="font-display font-bold">{r.name}</div>
          <div className="mt-2 text-xs text-muted-foreground">Average score</div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${r.avg ?? 0}%` }} /></div>
          <div className="mt-0.5 text-xs">{r.avg != null ? `${r.avg.toFixed(0)}%` : "No marks yet"}</div>
          <div className="mt-3 text-xs text-muted-foreground">Assignment completion</div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full bg-gold" style={{ width: `${r.completion}%` }} /></div>
          <div className="mt-0.5 text-xs">{r.completion}%</div>
        </div>
      ))}
      {!rows.length && <div className="text-sm text-muted-foreground">No learners.</div>}
    </div>
  );
}
